import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Link } from "gatsby";
import { AnchorLink } from "gatsby-plugin-anchor-links";
import { modifyColor, addAlpha } from "../utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";

const ExternalIcon = styled(FontAwesomeIcon)`
  margin-inline: 0.3rem;
`;
const StyledExternal = styled.a``;
const StyledLink = styled(Link)``;
const StyledAnchor = styled(AnchorLink)``;
const StyledButton = styled.button`
  outline: none;
`;
const ButtonBase = styled(
  ({ to, target, rel, children, dangerouslySetInnerHTML, ...props }) =>
    to === undefined ? (
      <StyledButton
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        {...props}
      >
        {children}
      </StyledButton>
    ) : target ? (
      <StyledExternal href={to} target={target} rel={rel} {...props}>
        {children ? (
          children
        ) : (
          <span dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}{" "}
        <ExternalIcon size={"sm"} icon={faArrowUpRightFromSquare} />
      </StyledExternal>
    ) : to?.includes("#") ? (
      <StyledAnchor to={to} {...props}>
        {children ? (
          children
        ) : (
          <span dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
        )}
      </StyledAnchor>
    ) : (
      <StyledLink
        to={to}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        {...props}
      >
        {children}
      </StyledLink>
    )
)`
  color: var(--text-color);
  background-color: var(--bg-color);
  border: 1px solid var(--border-color);
  font-size: 1rem;
  border-radius: ${({ $borderRadius }) =>
    $borderRadius || `var(--border-radius)`};
  line-height: initial;
  text-decoration: none;
  transition: 0.3s all ease-in-out;
  font-family: var(--font-paragraphs);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ $padding, $borderRadius }) =>
    $padding && $borderRadius
      ? $padding
      : $borderRadius
      ? "1rem 2.2rem"
      : $padding
      ? $padding
      : "1rem 2rem"};

  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      cursor: pointer;
      background-color: var(--color-hover-button);
      border-color: var(--border-color--hover, var(--color-hover-button));
      color: var(--color-white, var(--text-color, currentColor));
    }
  }

  &:active {
    background-color: var(--color-hover-button);
  }
  &.disabled,
  &:disabled {
    background-color: var(--bg-color--disabled);
    border-color: var(--border-color--disabled, var(--bg-color--disabled));
    color: var(--text-color--disabled, var(--text-color, currentColor));
    cursor: not-allowed;
    opacity: 0.6;
    @media (hover: hover) {
      &:hover {
        background-color: var(--bg-color--disabled);
        color: var(--text-color--disabled, var(--text-color, currentColor));
        border-color: var(--border-color--disabled, var(--bg-color--disabled));
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    margin-left: 0;
  }
`;
const Filled = styled(
  ({
    backgroundColor: backgroundColorIgnored,
    $textColor: textColorIgnored,
    ...rest
  }) => <ButtonBase {...rest} />
)`
  --bg-color: ${({ $backgroundColor }) =>
    $backgroundColor || `var(--color-primary)`};
  --bg-color--hover: ${({ $backgroundColor }) =>
    modifyColor($backgroundColor || `var(--color-body-text)`, 40)};
  --text-color: ${({ $textColor }) => $textColor || `var(--color-white)`};
  --border-color: ${({ $backgroundColor }) =>
    $backgroundColor || `var(--color-primary)`};
  --bg-color--disabled: ${({ $backgroundColor }) =>
    modifyColor($backgroundColor || `var(--color-body-text)`, 40)};
`;
const Outlined = styled(
  ({
    $backgroundColor: backgroundColorIgnored,
    $textColor: textColorIgnored,

    ...rest
  }) => <ButtonBase {...rest} />
)`
  --border-color: ${({ $textColor, $backgroundColor }) =>
    $textColor || $backgroundColor || `var(--color-primary)`};
  background-color: "transparent";
  color: ${({ $textColor, $backgroundColor }) =>
    $textColor || $backgroundColor || `var(--color-primary)`};
  --bg-color: transparent;
  --text-color: ${({ $textColor, $backgroundColor }) =>
    $textColor || $backgroundColor || `var(--color-primary)`};
  --bg-color--hover: ${({ $textColor, $backgroundColor }) =>
    addAlpha($textColor || $backgroundColor || `var(--color-primary)`, 0.3)};
  --border-color--hover: ${({ $textColor, $backgroundColor }) =>
    $textColor || $backgroundColor || `var(--color-primary)`};
  --text-color--hover: ${({ $textColor, $backgroundColor }) =>
    $textColor || $backgroundColor || `var(--color-primary)`};
  --text-color--disabled: ${({ $textColor, $backgroundColor }) =>
    modifyColor($textColor || $backgroundColor || `var(--color-primary)`, 70)};
  --border-color--disabled: ${({ $textColor, $backgroundColor }) =>
    modifyColor($textColor || $backgroundColor || `var(--color-primary)`, 70)};
`;

export default function Button({
  url,
  variant,
  label,
  innerClassName,
  target,
  rel,
  backgroundColor,
  textColor,
  disabled,
  children,
  onClick,
  borderRadius,
  padding,
  style,
  ...props
}) {
  const Button = variant === "filled" ? Filled : Outlined;
  return (
    <Button
      aria-label={label}
      to={url}
      target={target}
      rel={rel}
      className={disabled ? "disabled" : innerClassName}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      onClick={onClick}
      $borderRadius={borderRadius}
      $padding={padding}
      {...{
        props,
        ...(children
          ? { children }
          : { dangerouslySetInnerHTML: { __html: label } }),
      }}
      style={style}
    />
  );
}

Button.propTypes = {
  url: PropTypes.string,
  variant: PropTypes.oneOf(["filled", "outlined"]),
  label: PropTypes.string,
  innerClassName: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  borderRadius: PropTypes.string,
  padding: PropTypes.string,
  style: PropTypes.object,
};

Button.defaultProps = {
  innerClassName: undefined,
  variant: "filled",
};
